import { useEffect, useState } from 'react';
import { Head, useAdmin } from '../ui/Shell';
import { Block, Cell, Field, Grid, Kv, Seg, Table, type Col, type KvRow, type Row, type Tone } from '../ui/Block';
import { fmtBytes, fmtDate, post } from '../lib/api';
import { describeErr, hhmmss, useData } from '../lib/hooks';

type Kind = 'feature' | 'api' | 'security' | 'data' | 'rollback';
interface Policy { deprecatedBelow: string | null; unsupportedBelow: string | null; messageDeprecated: string | null; messageUnsupported: string | null }
interface Change extends Policy {
  id: string; kind: Kind; why: string; evidence: string; proposedBy: string;
  status: 'proposed' | 'approved' | 'rejected' | 'applied';
  decidedBy: string | null; decidedAt: string | null; decisionNote: string | null; previous: Policy | null; createdAt: string;
}
interface ReleaseRow { release: { version: string; publishedAt: string | null; url: string | null; bytes: number | null }; level: 'supported' | 'deprecated' | 'unsupported'; offered: boolean }
interface View { client: string; policy: Policy; latest: { version: string } | null; source: string; releases: ReleaseRow[]; kinds: Kind[]; pending: Change[]; history: Change[] }

const CLIENTS = [{ value: 'mac_app', label: 'VocalStack for Mac' }];
const LEVEL_TONE: Record<ReleaseRow['level'], Tone> = { supported: 'ok', deprecated: 'warn', unsupported: 'bad' };
const STATUS_TONE: Record<Change['status'], Tone> = { proposed: 'info', approved: 'ok', applied: 'ok', rejected: 'mute' };
const KIND_HELP: Record<Kind, string> = {
  feature: 'A newer feature: may deprecate, never makes a version unsupported.',
  api: 'An API change older versions cannot follow: may make versions unsupported.',
  security: 'A security fix: may make versions unsupported.',
  data: 'A data change older versions would break: may make versions unsupported.',
  rollback: 'Undo: the only kind that lowers a threshold.',
};

const thresholds = (p: Policy | null) => (p ? `deprecated below ${p.deprecatedBelow ?? '—'} · unsupported below ${p.unsupportedBelow ?? '—'}` : '—');

export function ClientVersions() {
  const ctx = useAdmin();
  const { env, mobile, paused } = ctx;
  const [client, setClient] = useState('mac_app');
  const d = useData<View>(env, `/admin/clients/${client}/versions`, 60, paused, [client]);
  const v = d.data;

  const blank = { deprecatedBelow: '', unsupportedBelow: '', messageDeprecated: '', messageUnsupported: '', kind: 'feature' as Kind, why: '', evidence: '' };
  const [form, setForm] = useState(blank);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => {
    if (!v || dirty) return;
    setForm({ ...blank, deprecatedBelow: v.policy.deprecatedBelow ?? '', unsupportedBelow: v.policy.unsupportedBelow ?? '', messageDeprecated: v.policy.messageDeprecated ?? '', messageUnsupported: v.policy.messageUnsupported ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [v]);
  const set = (k: keyof typeof blank, val: string) => { if (mobile) return; setForm((f) => ({ ...f, [k]: val })); setDirty(true); setRefusal(null); };

  const act = async (key: string, path: string, body: unknown, text: string) => {
    setBusy(key);
    try {
      await post(env, path, body);
      ctx.toast(text, 'In force within a minute');
      setRefusal(null);
      if (key === 'edit') setDirty(false);
      d.reload();
    } catch (e) {
      const m = (e as Error).message;
      if (key === 'edit') setRefusal(m);
      ctx.toast(m, '', { logged: false });
    } finally { setBusy(null); }
  };

  const err = describeErr(d.error) ? { ...describeErr(d.error)!, retry: d.reload } : null;
  const policyRows: KvRow[] = v ? [
    { k: 'Deprecated below', kSub: 'works, without the newer features', v: { t: v.policy.deprecatedBelow ?? 'none', mono: true } },
    { k: 'Unsupported below', kSub: 'blocked until updated', v: { t: v.policy.unsupportedBelow ?? 'none', mono: true } },
    { k: 'Offered update', v: v.latest ? { t: v.latest.version, mono: true } : { t: 'none', fg: 'var(--ink-45)' } },
    { k: 'Deprecated copy', v: { t: v.policy.messageDeprecated ?? "the app's own", fg: v.policy.messageDeprecated ? undefined : 'var(--ink-45)' } },
    { k: 'Unsupported copy', v: { t: v.policy.messageUnsupported ?? "the app's own", fg: v.policy.messageUnsupported ? undefined : 'var(--ink-45)' } },
    { k: 'Releases from', v: { t: v.source, mono: true } },
  ] : [];

  const relCols: Col[] = [{ label: 'Version', width: '18%' }, { label: 'Level', width: '20%' }, { label: 'Published', width: '24%' }, { label: 'Update', width: '38%' }];
  const relRows: Row[] = (v?.releases ?? []).map((r) => ({
    key: r.release.version,
    cells: [
      { t: r.release.version, mono: true, fw: r.offered ? 600 : 400 },
      { pill: { label: r.level.toUpperCase(), tone: LEVEL_TONE[r.level] } },
      { t: fmtDate(r.release.publishedAt) },
      r.release.url ? { t: r.offered ? 'offered' : 'signed zip', sub: r.release.bytes ? fmtBytes(r.release.bytes) : undefined } : { t: 'no update file', fg: 'var(--ink-45)' },
    ],
  }));

  const changeCols: Col[] = [{ label: 'When', width: '14%' }, { label: 'Change', width: '30%' }, { label: 'Why and evidence', width: '34%' }, { label: 'Status', width: '22%' }];
  const changeRow = (c: Change, pending: boolean): Row => ({
    key: c.id,
    cells: [
      { t: fmtDate(c.createdAt), sub: c.proposedBy },
      { t: thresholds(c), sub: <span><span className="mono">{c.kind}</span>{c.previous ? ` · was ${thresholds(c.previous)}` : ''}</span> },
      { t: c.why, sub: c.evidence },
      pending
        ? { acts: [
            { label: 'Approve', primary: true, disabled: mobile || busy !== null, onClick: () => void act(c.id, `/admin/clients/${client}/versions/${c.id}/approve`, {}, `Approved: ${thresholds(c)}`) },
            { label: 'Reject', danger: true, disabled: mobile || busy !== null, onClick: () => { const note = window.prompt('Why reject it? (optional)') ?? undefined; void act(c.id, `/admin/clients/${client}/versions/${c.id}/reject`, { note }, 'Proposal rejected'); } },
          ] }
        : { pill: { label: c.status.toUpperCase(), tone: STATUS_TONE[c.status] }, sub: [c.decidedBy && c.status !== 'applied' ? `by ${c.decidedBy}` : null, c.decisionNote].filter(Boolean).join(' · ') || undefined },
    ],
  });

  const body = { ...form, deprecatedBelow: form.deprecatedBelow.trim() || null, unsupportedBelow: form.unsupportedBelow.trim() || null, messageDeprecated: form.messageDeprecated.trim() || null, messageUnsupported: form.messageUnsupported.trim() || null };
  const versions = (v?.releases ?? []).map((r) => r.release.version).join(', ');

  return (
    <>
      <Head title="Client versions" sub="Which versions of VocalStack's apps are deprecated or unsupported. Your edits apply at once; an AI session's proposal changes nothing until you approve it." />
      <div style={{ marginBottom: 12 }}><Seg<string> value={client} options={CLIENTS} onChange={setClient} /></div>
      <Grid>
        <Cell span={5}>
          <Block title="In force" sub="What GET /v1/clients/mac_app answers" asOf={hhmmss(d.at)} loading={d.loading && !v} error={err}>
            <Kv rows={policyRows} kw="42%" />
          </Block>
        </Cell>
        <Cell span={7}>
          <Block title="Releases" count={v?.releases.length} sub="Every version tag; the newest carry a signed update" loading={d.loading && !v} error={err} empty={v && v.releases.length === 0 ? 'No release seen yet: the website deploy publishes them.' : undefined}>
            <Table cols={relCols} rows={relRows} mobile={mobile} />
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Pending proposals" count={v?.pending.length} sub="Filed by AI sessions; checked again against the policy in force when you approve" loading={d.loading && !v} error={err} empty={v && v.pending.length === 0 ? 'Nothing waiting.' : undefined} emptyGood>
            <Table cols={changeCols} rows={(v?.pending ?? []).map((c) => changeRow(c, true))} mobile={mobile} />
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="Edit" sub="Applies at once. Every change carries why and evidence, and is kept in the history." note={mobile ? 'Editing is desktop-only.' : undefined}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 14px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <Field label="Deprecated below" mono placeholder="none" value={form.deprecatedBelow} onChange={(x) => set('deprecatedBelow', x)} hint={versions ? `Released: ${versions}` : undefined} />
                <Field label="Unsupported below" mono placeholder="none" value={form.unsupportedBelow} onChange={(x) => set('unsupportedBelow', x)} />
                <Field label="Deprecated copy (optional)" placeholder="the app's own" value={form.messageDeprecated} onChange={(x) => set('messageDeprecated', x)} />
                <Field label="Unsupported copy (optional)" placeholder="the app's own" value={form.messageUnsupported} onChange={(x) => set('messageUnsupported', x)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <Seg<Kind> value={form.kind} options={(v?.kinds ?? Object.keys(KIND_HELP) as Kind[]).map((k) => ({ value: k, label: k }))} onChange={(k) => set('kind', k)} mono />
                <span style={{ fontSize: 12, fontWeight: 300, color: 'var(--ink-62)' }}>{KIND_HELP[form.kind]}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 }}>
                <Field label="Why" placeholder="One sentence: what the change is for" value={form.why} onChange={(x) => set('why', x)} />
                <Field label="Evidence" placeholder="The feature, API change, commit or issue" value={form.evidence} onChange={(x) => set('evidence', x)} />
              </div>
              {refusal && <div style={{ fontSize: 12, color: 'var(--conf-low)' }}>{refusal}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn--sm btn--primary" disabled={mobile || !dirty || busy !== null} onClick={() => void act('edit', `/admin/clients/${client}/versions`, body, `In force: ${thresholds(body as Policy)}`)}>{busy === 'edit' ? 'Applying…' : 'Apply'}</button>
              </div>
            </div>
          </Block>
        </Cell>
        <Cell span={12}>
          <Block title="History" count={v?.history.length} sub="Append-only: edits, approvals and rejections, newest first" loading={d.loading && !v} error={err} empty={v && v.history.length === 0 ? 'No change yet.' : undefined}>
            <Table cols={changeCols} rows={(v?.history ?? []).map((c) => changeRow(c, false))} mobile={mobile} />
          </Block>
        </Cell>
      </Grid>
    </>
  );
}
